// runAnimateAllImages — the gated fan-out executor for the agent's
// `animateAllImagesInScene` mutation. Called by BulkMutateToolUI on the user's
// Apply click (never auto-applied). Enumerates the image boxes of the scene and
// POSTs one Aidea Wizard animation job per image, mirroring ImageAnimatorModal's
// `handleSubmit` animate payload + the `api/animation/v2` endpoint.
//
// Returns { jobs: [{ boxId, jobId }], errors: [{ boxId, error }] }. Progress is
// surfaced by the card via the existing session-jobs socket (useSessionImageJobs),
// matched on the returned jobIds.

import { getToken } from "../../../common/environment_config";
import { isCreditRefusal, notifyInsufficientBalance } from "../../utils/insufficientBalance";

// Read the editor's training id from the `?id=` URL param (same source the
// animator modal + useSessionImageJobs use as the backend room/training key).
function readTrainingId() {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get("id") || null;
  } catch {
    return null;
  }
}

// Enumerate the image boxes of a scene. `pluginToolbarsById[id].pluginId` is the
// authoritative plugin-type source (per aidea-middleware/CLAUDE.md); fall back to
// `boxesById[id].pluginId`. An image box is HotspotImages with a `state.url`.
// Returns [{ boxId, imageUrl }].
function collectImageBoxes(present, navId) {
  const navItem = present && present.navItemsById && present.navItemsById[navId];
  const boxIds = (navItem && Array.isArray(navItem.boxes) && navItem.boxes) || [];
  const pluginToolbars = (present && present.pluginToolbarsById) || {};
  const boxesById = (present && present.boxesById) || {};

  const out = [];
  for (const boxId of boxIds) {
    const toolbar = pluginToolbars[boxId];
    const box = boxesById[boxId];
    const pluginId = (toolbar && toolbar.pluginId) || (box && box.pluginId);
    if (pluginId !== "HotspotImages") continue;
    const state = (toolbar && toolbar.state) || (box && box.state) || {};
    const imageUrl = typeof state.url === "string" ? state.url : "";
    if (!imageUrl) continue;
    out.push({ boxId, imageUrl });
  }
  return out;
}

/**
 * Fan out one animation job per image box in the scene.
 *
 * @param {Function} _dispatch unused (kept for the executor signature parity)
 * @param {Function} getState  returns the editor Redux state
 * @param {Object} params      { provider, durationSec, instruction? }
 * @param {Object} scope       { navId }
 * @returns {Promise<{ jobs: Array<{boxId, jobId}>, errors: Array<{boxId, error}> }>}
 */
export async function runAnimateAllImages(_dispatch, getState, params, scope) {
  const jobs = [];
  const errors = [];

  const state = typeof getState === "function" ? getState() : null;
  const present = state && state.undoGroup && state.undoGroup.present;
  const navId = scope && scope.navId;
  if (!present || !navId) {
    return { jobs, errors };
  }

  const images = collectImageBoxes(present, navId);
  if (!images.length) {
    return { jobs, errors };
  }

  // VERIFY-LIVE: real endpoint + auth. Mirrors ImageAnimatorModal.handleSubmit
  // (process.env.MW_API_URL + Bearer getToken() + api/animation/v2 body shape);
  // can't be exercised against the live backend in this environment.
  const apiUrl = process.env.MW_API_URL;
  const trainingId = readTrainingId();

  for (let index = 0; index < images.length; index++) {
    const { boxId, imageUrl } = images[index];
    try {
      const body = {
        imageUrl,
        provider: params.provider,
        prompt: params.instruction || "",
        outputs: ["webp"],
        loop: false,
        durationSec: params.durationSec,
        trainingId,
        boxId,
        navId,
        sceneIndex: String(index),
      };
      const response = await fetch(`${apiUrl}api/animation/v2`, {
        method: "POST",
        headers: {
          // Read per request: the loop runs one POST after another and can
          // outlast the access token it started with.
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const rawMsg = (errData.message || `HTTP ${response.status}`).toString();
        // A zero/short balance fails EVERY image identically, so surface the
        // global balance popup once and stop instead of hammering the endpoint
        // N times and returning N copies of the same error to the card.
        if (isCreditRefusal(rawMsg, errData)) {
          notifyInsufficientBalance({ ...errData, message: errData.message || rawMsg });
          errors.push({ boxId, error: rawMsg });
          break;
        }
        throw new Error(rawMsg);
      }
      const result = await response.json();
      if (result && result.jobId) {
        jobs.push({ boxId, jobId: result.jobId });
      } else {
        errors.push({ boxId, error: "No jobId in response" });
      }
    } catch (err) {
      errors.push({ boxId, error: (err && err.message) || String(err) });
    }
  }

  return { jobs, errors };
}

export default runAnimateAllImages;
